import React, { ReactElement, SVGProps, useState } from 'react'

import SvgIcon from '@mui/material/SvgIcon'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Close from '@tdcerhverv/parrotfish/dist/icons/Icons/Close.svg'
import Error_filled from '@tdcerhverv/parrotfish/dist/icons/Icons/Error_filled.svg'
import Success_filled from '@tdcerhverv/parrotfish/dist/icons/Icons/Success_filled.svg'
import { ConditionalWrapper } from '../../utils'
import { useConsent } from '../../../hooks/useConsent'
import UsabillaFeedback from '../../UsabillaFeedback'
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material'
import RichText, { IRichText } from '../../RichText'
import { IParrotfishIcon } from '../../ParrotfishIcon/ParrotfishIcon'
import Image, { IImage } from '../../Image'
import { ContentfulToComponent, ContentfulToComponentProps } from '../../RichText/types'
import CallToAction from '../../CallToAction'

export interface ICustomIcons {
  CustomErrorIcon: (props: SVGProps<SVGElement>) => ReactElement
  CustomSuccessIcon: (props: SVGProps<SVGElement>) => ReactElement
}

export interface FeedbackMessageProps {
  isError?: boolean
  content: FeedbackContent
  card?: boolean
  usabillaId?: string
}

export interface FeedbackContent {
  onClick?: () => void
  internalName?: string
  title?: string
  body?: IRichText
  icon?: IImage | IParrotfishIcon
  showPopupOnPage?: boolean
  buttonLabel: string
}

const FeedbackMessage: React.FC<FeedbackMessageProps> = ({ isError, content, card = false, usabillaId }) => {
  const { cookie_cat_statistic } = useConsent()
  const showFeedbackWidget = cookie_cat_statistic && usabillaId && !isError
  const { buttonLabel, body, onClick, title, internalName, icon, showPopupOnPage } = content
  const [isPopupVisible, setPopupVisible] = useState(true)
  const [isFeedbackClosed, setFeedbackClosed] = useState(false)

  const propsMap: Partial<ContentfulToComponentProps> = {
    ContentfulComponentCta: <CallToActionType,>(props: CallToActionType) => ({
      ...(props as unknown as CallToActionType),
    }),
  } as const

  const feebackComponentMap: Partial<ContentfulToComponent> = {
    'ContentfulComponentCta': CallToAction,
  } as const

  const styles = {
    illustration: {
      '& > svg': {
        width: 'fit-content',
        height: 'fit-content',
      },
    },
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      textAlign: 'center',
    },
    card: {
      textAlign: 'center',
      width: '300px',
      height: '300px',
      mx: 'auto',
    },
    dialog: { width: '350px' },
    content: { textAlign: 'center' },
    imageStyle: {
      height: '150px',
    },
    button: {
      m: '24px auto 0 !important',
    }
  }

  const handleClosePopup = () => {
    setPopupVisible(false)
    setFeedbackClosed(true)
  }

  if (showPopupOnPage && isPopupVisible) {
    return (
      <Dialog open={isPopupVisible} PaperProps={{ sx: styles.dialog }}>
        <DialogTitle>
          <IconButton onClick={handleClosePopup}>
            <SvgIcon component={Close} fontSize="inherit" viewBox={'0 0 48 48'} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={styles.content}>
          {icon ? (
            <Image {...(icon as IImage)} imageStyles={styles.imageStyle} />
          ) : (
            <SvgIcon
              component={isError ? Error_filled : Success_filled}
              viewBox="0 0 48 48"
              sx={{ color: isError ? 'error.dark' : 'success.main', fontSize: 40 }}
            />
          )}
          <Typography
            variant="subtitle1"
            sx={{ color: isError ? 'error.dark' : 'success.main', mt: '24px', mb: '16px' }}
          >
            {title}
          </Typography>
          <RichText
            {...(body as IRichText)}
            componentMap={feebackComponentMap as ContentfulToComponent}
            propsMap={propsMap as ContentfulToComponentProps}
          />
          {showFeedbackWidget && <UsabillaFeedback widgetId="b29dc9281420" containerId={usabillaId} />}
          {buttonLabel && (
            <Button sx={styles.button} variant={!isError ? 'outlined' : 'contained'} onClick={onClick}>
              {buttonLabel}
            </Button>
          )}
        </DialogContent>
      </Dialog>
    )
  }
  if (isFeedbackClosed) {
    return null
  }

  return (
    <ConditionalWrapper
      condition={card}
      wrapper={children => (
        <Card raised>
          <CardContent sx={styles.card}>{children}</CardContent>
        </Card>
      )}
    >
      <Box sx={styles.wrapper} id={internalName}>
        {icon ? (
          <Image {...(icon as IImage)} imageStyles={styles.imageStyle} />
        ) : (
          <SvgIcon
            component={isError ? Error_filled : Success_filled}
            viewBox="0 0 48 48"
            sx={{ color: isError ? 'error.dark' : 'success.main', fontSize: 40 }}
          />
        )}
        <Typography variant="subtitle1" sx={{ color: isError ? 'error.dark' : 'success.main', mt: '24px', mb: '16px' }}>
          {title}
        </Typography>
        <RichText
          {...(body as IRichText)}
          componentMap={feebackComponentMap as ContentfulToComponent}
          propsMap={propsMap as ContentfulToComponentProps}
        />
        {showFeedbackWidget && <UsabillaFeedback widgetId="b29dc9281420" containerId={usabillaId} />}
        {buttonLabel && (
          <Button sx={styles.button} variant={!isError ? 'outlined' : 'contained'} onClick={onClick}>
            {buttonLabel}
          </Button>
        )}
      </Box>
    </ConditionalWrapper>
  )
}

export default FeedbackMessage
